import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Button } from "@mui/material";
import React, { useMemo, useState } from "react";

import { Typography } from "new-components/typography/Typography";
import { Detector } from "types/detectors";
import { generateId } from "utils/generateId";

import { FilterComponent } from "./FilterComponent";
import { FilterParams } from "./FiltersData";
import { removeEmptyValues } from "./utils";

export interface FilterChanges {
  id: string;
  columnField: string;
  operatorValue: string | null;
  value: string | string[];
}

export interface GridFiltersProps {
  detector?: Detector;
  isEdit: boolean;
  filterState: Record<string, any>;
  currentFilter?: FilterChanges[];
  onFilterChange: (filterValues: FilterChanges[]) => void;
  handleClose: () => void;
  filterableElements: FilterParams[];
}

const getDefaultFilter = (filterableElements: FilterParams[], field: string) => {
  const findFilter = filterableElements.find((p) => p.columnField === field);
  return {
    id: generateId(),
    columnField: field,
    operatorValue: (findFilter?.operatorOptions && findFilter?.operatorOptions[0].value) || null,
    value: findFilter?.valueOptions?.at(0)?.value || "",
  } as FilterChanges;
};

const getDefaultChanges = (filterableElements: FilterParams[], filterState: FilterChanges[], isEdit: boolean) => {
  if (!isEdit) {
    const usedFields = filterState.map((item: FilterChanges) => item.columnField);
    const availableFilter = filterableElements.find((filter) => !usedFields.includes(filter.columnField));

    if (availableFilter) {
      return [getDefaultFilter(filterableElements, availableFilter.columnField)];
    }
    return [];
  }

  const mapChanges = filterState.map((item) => ({
    ...item,
    id: generateId(),
  }));
  return mapChanges;
};

export function GridFilters(props: GridFiltersProps) {
  const { detector, isEdit, filterState, currentFilter, onFilterChange, handleClose, filterableElements } = props;

  const [changes, setChanges] = useState<FilterChanges[]>(
    getDefaultChanges(filterableElements, isEdit ? currentFilter : filterState.filterModel.items, isEdit)
  );

  const allFilterChanges = useMemo(() => [...filterState.filterModel.items, ...changes], [filterState, changes]);

  const isDuplicate = (columnField: string) => {
    const usedFields = allFilterChanges.map((item) => item.columnField);
    return Boolean(usedFields.includes(columnField));
  };

  const handleClickAdd = () => {
    const usedFields = allFilterChanges.map((item: FilterChanges) => item.columnField);
    const availableFilter = filterableElements.find((filter) => !usedFields.includes(filter.columnField));

    if (!availableFilter) {
      return;
    }
    const defaultItem = getDefaultFilter(filterableElements, availableFilter.columnField);
    setChanges((changes: any) => [...changes, defaultItem]);
  };

  const handleChangeColumn = (columnField: string, id: string) => {
    if (isDuplicate(columnField) || isEdit) {
      return;
    }

    const newChanges = changes.map((option) => {
      if (option.id === id) {
        const defaultFilter = getDefaultFilter(filterableElements, columnField);
        return { ...defaultFilter, id };
      }
      return option;
    });
    setChanges(newChanges);
  };

  const handleChangeFilterValue = (columnField: string, value: Record<string, string | string[]>) => {
    const applyChanges = changes.map((item) => {
      if (item.columnField === columnField) {
        return {
          ...item,
          ...value,
        };
      }

      return item;
    });
    setChanges(applyChanges);
  };

  const handleRemoveFilter = (columnField: string) => {
    const removeItem = changes.filter((item) => item.columnField !== columnField);
    setChanges(removeItem);
  };

  const handleClickRemoveAll = () => {
    setChanges([]);
    handleClose();
  };

  const handleSubmitChanges = () => {
    const addNewFilter = () => {
      const values = removeEmptyValues(changes);
      return [...filterState.filterModel.items, ...values];
    };

    const editFilter = () => {
      const currentItem = changes[0];
      const modifyItems = filterState.filterModel.items.map((item: FilterChanges) => {
        if (item.columnField === currentItem.columnField) {
          return {
            ...item,
            ...currentItem,
          };
        }

        return item;
      });
      return removeEmptyValues(modifyItems);
    };

    const newChanges = isEdit ? editFilter() : addNewFilter();
    onFilterChange(newChanges);
    handleClose();
  };

  return (
    <Box className="p-2">
      {changes.length > 0 ? (
        <Box className="flex justify-start flex-col gap-4 mb-4">
          {changes.map((option) => (
            <FilterComponent
              key={option.id}
              detector={detector}
              filterId={option.id}
              columnField={option.columnField}
              filterValue={option.value}
              filterOperator={option.operatorValue || undefined}
              handleChangeColumn={handleChangeColumn}
              handleChangeFilter={handleChangeFilterValue}
              handleRemoveFilter={handleRemoveFilter}
              isEdit={isEdit}
              filterChanges={[...filterState.filterModel.items]}
              filterableElements={filterableElements}
            />
          ))}
        </Box>
      ) : (
        <Box className="m-4">
          <Typography fontWeight="semibold" className="text-neutral-medium">
            No Filters selected
          </Typography>
        </Box>
      )}
      <Box className="flex justify-between gap-10">
        <Box className="flex gap-4">
          {isEdit && <Button onClick={handleClose}>Cancel</Button>}

          {!isEdit && (
            <>
              <Button startIcon={<AddIcon />} onClick={handleClickAdd}>
                <Typography className="hidden sm:inline-block">Add Filter</Typography>
              </Button>

              {changes.length > 0 && (
                <Button startIcon={<DeleteForeverIcon />} onClick={handleClickRemoveAll}>
                  <Typography className="hidden sm:inline-block">Cancel filters</Typography>
                </Button>
              )}
            </>
          )}
        </Box>
        <Button startIcon={<FilterListIcon />} onClick={handleSubmitChanges}>
          <Typography className="hidden sm:inline-block">Apply Filters</Typography>
        </Button>
      </Box>
    </Box>
  );
}
